"use client";
import { JSX, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styles from "./DimensionHeroGallery.module.scss";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";

interface DimensionHeroGalleryProps {
  heroImages: JSX.Element[];
  className?: string;
  makeName?: string;
  modelName?: string;
  pageType?: string;
  carAvailability?: string;
}

export default function DimensionHeroGallery({
  heroImages,
  className,
  makeName,
  modelName,
  pageType,
  carAvailability,
}: DimensionHeroGalleryProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const isFirst = currentImageIndex === 0;
  const isLast = currentImageIndex === heroImages?.length - 1;

  // Event tracking for gallery navigation
  const trackGalleryNavigation = async (action: string, direction?: string) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.DIMENSIONS_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: action,
        current_image_index: currentImageIndex + 1,
        total_images: heroImages.length,
        direction: direction,
        vehicle_make: makeName,
        vehicle_model: modelName,
        car_availability: carAvailability,
      },
    });
  };

  // Unified navigation function that handles both bounded and circular navigation
  const navigateToImage = async (
    direction: "next" | "prev",
    navigationType: "bounded" | "circular" = "bounded"
  ) => {
    const action =
      direction === "next" ? "tap_btn_gallery_next" : "tap_btn_gallery_prev";

    if (navigationType === "bounded") {
      // Bounded navigation (mobile/touch) - stops at first/last
      if (
        (direction === "next" && isLast) ||
        (direction === "prev" && isFirst)
      ) {
        return;
      }
    }

    // Update image index based on navigation type
    setCurrentImageIndex((prev) => {
      if (navigationType === "circular") {
        // Circular navigation (desktop) - wraps around
        return direction === "next"
          ? (prev + 1) % heroImages.length
          : (prev - 1 + heroImages.length) % heroImages.length;
      } else {
        // Bounded navigation
        return direction === "next" ? prev + 1 : prev - 1;
      }
    });

    await trackGalleryNavigation(action, direction);
  };

  // Mobile/touch navigation (bounded)
  const next = () => navigateToImage("next", "bounded");
  const prev = () => navigateToImage("prev", "bounded");

  // Desktop navigation (circular)
  const nextImage = () => navigateToImage("next", "circular");
  const prevImage = () => navigateToImage("prev", "circular");

  const goToImage = async (index: number) => {
    await trackGalleryNavigation(
      "tap_btn_gallery_nav_dot",
      index > currentImageIndex ? "next" : "prev"
    );
    setCurrentImageIndex(index);
  };

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = async () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const distance = touchStartX.current - touchEndX.current;
      if (distance > 50) {
        // Swiped left
        await next();
      } else if (distance < -50) {
        // Swiped right
        await prev();
      }
    }
    // Reset
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div className={`${className || ""}`}>
      <div className={styles.mobileLayout}>
        <div
          className={styles.mobileImageSection}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {heroImages?.[currentImageIndex]}

          <div className={styles.mobileNavigation}>
            {heroImages?.map((_, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                aria-label={`Go to image ${index + 1}`}
                className={`${styles.navBar} ${
                  index === currentImageIndex ? styles.navBarActive : ""
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className={styles.desktopLayout}>
        <div className={styles.desktopImageSection}>
          {heroImages?.[currentImageIndex]}

          <div className={styles.desktopNavigation}>
            <span className={styles.imageCounter}>
              {`${currentImageIndex + 1}/${heroImages?.length}`}
            </span>
            <button onClick={prevImage} className={styles.navButton} aria-label="Previous image">
              <ChevronLeft className={styles.navIcon} />
            </button>
            <button onClick={nextImage} className={styles.navButton} aria-label="Next image">
              <ChevronRight className={styles.navIcon} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
